import React from 'react';
import PropTypes from 'prop-types';
import { Link, graphql } from 'gatsby';
import styled from 'styled-components';

import Layout from '../components/layout';
import SEO from '../components/seo';
import { GridItem, Grid } from '../components/Grid';
import PostCard from '../components/Blog/PostCard';
import PostCardHighlight from '../components/Blog/PostCardHighlight';
import Hero from '../components/Hero';

const BlogSection = styled.div`
  margin-bottom: 2.4375rem;

  a:hover h3 {
    color: #000000;
  }

  p {
    margin-bottom: 0px;
  }

  h2 {
    margin-bottom: 1.437rem;
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
    line-height: 1.8125rem;
  }

  &:last-child {
    margin-bottom: 8.3125rem;
  }
`;

const BlogIndex = ({ data, location }) => {
  const { customerStoriesBlogPosts, allContentfulCategory } = data;
  const customerStoriesPosts = customerStoriesBlogPosts.edges;
  const getCategoryFromSlug = slug => allContentfulCategory.edges.filter(
    category => category.node.slug === slug
  )[0].node;
  const productPostHighlight = getCategoryFromSlug('product')
    .postHighlight[0];
  const customerStoriesPostHighlight = getCategoryFromSlug('customer-stories')
    .postHighlight[0];
  return (
    <Layout location={location}>
      <SEO title="Discover Workast" />
      <Hero featuredImage={customerStoriesPostHighlight.featuredImage}>
        <h1>
          <Link to={`/blog/${customerStoriesPostHighlight.slug}`}>
            {customerStoriesPostHighlight.title}
          </Link>
        </h1>
      </Hero>
      <BlogSection>
        <h2>Customer Stories</h2>
        <Grid gridGap="4rem" minCol="280px">
          {customerStoriesPosts.map(({ node }) => {
            const title = node.title || node.slug;

            return (
              <GridItem key={node.slug}>
                <PostCard
                  title={title}
                  slug={node.slug}
                  featuredImage={node.featuredImage}
                  excerpt={node.excerpt.excerpt}
                />
              </GridItem>
            );
          })}
        </Grid>
      </BlogSection>
      <BlogSection>
        <h2>Product</h2>
        <PostCardHighlight
          title={productPostHighlight.title}
          slug={productPostHighlight.slug}
          featuredImage={productPostHighlight.featuredImage}
          excerpt={productPostHighlight.excerpt.excerpt}
          highlight
        />
      </BlogSection>
    </Layout>
  );
};

BlogIndex.propTypes = {
  data: PropTypes.shape({
    customerStoriesBlogPosts: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            title: PropTypes.string.isRequired,
            slug: PropTypes.string.isRequired,
            excerpt: PropTypes.shape({
              excerpt: PropTypes.string,
            }),
            featuredImage: PropTypes.shape({
              fluid: PropTypes.shape({
                aspectRatio: PropTypes.number,
                base64: PropTypes.string,
                sizes: PropTypes.string,
                src: PropTypes.string,
                srcSet: PropTypes.string,
                srcSetWebp: PropTypes.string,
                srcWebp: PropTypes.string,
              }),
              description: PropTypes.string,
            }),
          }),
        })
      ),
    }).isRequired,
    allContentfulCategory: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            title: PropTypes.string,
            slug: PropTypes.string,
            postHighlight: PropTypes.arrayOf(
              PropTypes.shape({
                title: PropTypes.string,
                slug: PropTypes.string,
                excerpt: PropTypes.shape({
                  excerpt: PropTypes.string,
                }),
                featuredImage: PropTypes.shape({
                  description: PropTypes.string,
                  fluid: PropTypes.shape({
                    aspectRatio: PropTypes.number,
                    base64: PropTypes.string,
                    sizes: PropTypes.string,
                    src: PropTypes.string,
                    srcSet: PropTypes.string,
                    srcSetWebp: PropTypes.string,
                    srcWebp: PropTypes.string,
                  }),
                }),
              })
            ),
          }),
        })
      ),
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    host: PropTypes.string,
    hostname: PropTypes.string,
    href: PropTypes.string,
    key: PropTypes.string,
    origin: PropTypes.string,
    pathname: PropTypes.string,
    port: PropTypes.string,
  }).isRequired,
};

export default BlogIndex;

export const pageQuery = graphql`
  query {
    customerStoriesBlogPosts: allContentfulBlogPost(
      filter: {
        category: { elemMatch: { slug: { eq: "customer-stories" } } }
        node_locale: { eq: "en-US" }
      }
      sort: { fields: [publishDate], order: DESC }
      limit: 3
    ) {
      edges {
        node {
          title
          slug
          excerpt {
            excerpt
          }
          featuredImage {
            description
            fluid(maxWidth: 1200) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
        }
      }
    }
    allContentfulCategory {
      edges {
        node {
          title
          slug
          postHighlight {
            title
            slug
            excerpt {
              excerpt
            }
            featuredImage {
              description
              fluid(maxWidth: 1200) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
      }
    }
  }
`;
